import CampaignStatus from '@/Enums/CampaignManagement/CampaignStatus.enum';
import { IAudienceControlOptionsByChannel } from '@/Interfaces/Audiences/Audience.interface';
import { ILineItemCreative } from '@/Interfaces/CampaignManagement/LineItem.interface';
import { IProductsByChannel } from '@/Interfaces/Product.interface';
import { LineItemChannels } from '@/constants/campaignManagement';
import { RootState } from '@/store';
import { createSelector } from '@reduxjs/toolkit';
import { selectVisibleCampaignManagementChannels } from './currentWhitelabel.selectors';
import { selectAllowCampaignApproval } from './me.selectors';

export const selectCampaignManagement = (state: RootState) => state.campaignManagement;

const selectActiveCampaign = createSelector([selectCampaignManagement], ({ activeCampaign }) => activeCampaign);

export const selectActiveCampaignLoading = createSelector(
  [selectCampaignManagement],
  ({ activeCampaignRequestId }) => !!activeCampaignRequestId,
);

export const selectActiveCampaignAdvertiser = createSelector(
  [selectActiveCampaign],
  // @ts-expect-error campaignManagement state is not typed
  activeCampaign => activeCampaign?.advertiser,
);

export const selectLineItem = createSelector(
  [selectActiveCampaign, (_, index: number) => ({ index })],
  // @ts-expect-error campaignManagement state is not typed
  (activeCampaign, { index }) => activeCampaign?.lineItems?.[index],
);

export const selectLineItemChannel = createSelector([selectLineItem], lineItem => lineItem?.channel?.name);

export const selectLineItemForecast = createSelector([selectLineItem], lineItem => lineItem?.forecast);

export const selectLineItemCreatives = createSelector(
  [selectLineItem],
  (lineItem): ILineItemCreative[] => lineItem?.creatives ?? [],
);

export const selectFirstPartySegments = createSelector(
  [selectCampaignManagement],
  ({ activeCampaign, beeswaxFirstPartySegments, beeswaxFirstPartySegmentsRequestId }) => ({
    // @ts-expect-error campaignManagement state is not typed
    firstPartySegments: beeswaxFirstPartySegments[activeCampaign?.advertiser?.id] ?? [],
    loadingFirstPartySegments: !!beeswaxFirstPartySegmentsRequestId,
  }),
);

export const selectActiveCampaignRequiresApproval = createSelector(
  [selectActiveCampaign, selectAllowCampaignApproval],
  (activeCampaign, allowCampaignApproval) => {
    return (
      // @ts-expect-error campaignManagement state is not typed
      activeCampaign?.status === CampaignStatus.APPROVAL_REQUIRED && allowCampaignApproval
    );
  },
);

export const selectTotalImpressionsBookedForActiveCampaign = createSelector([selectActiveCampaign], activeCampaign =>
  // @ts-expect-error campaignManagement state is not typed
  (activeCampaign?.lineItems ?? []).reduce((acc, cur) => (acc += cur.impressions_budget), 0),
);

//TODO: fix types once campaign management slice migrates to ts
export const selectCampaignManagementProducts = createSelector(
  [selectCampaignManagement],
  ({ products, productsRequestId }) =>
    ({ products, loading: !!productsRequestId }) as unknown as { products: IProductsByChannel; loading: boolean },
);

export const selectCampaignManagementFilters = createSelector([selectCampaignManagement], ({ filters }) => filters);

export const selectCampaignManagementIsArchivedFilter = createSelector(
  [selectCampaignManagementFilters],
  ({ isArchived }) => isArchived,
);

export const selectCampaignManagementAudiences = createSelector(
  [selectCampaignManagement],
  ({ audiences, audiencesRequestId }) => ({
    audiences: audiences as unknown as IAudienceControlOptionsByChannel<number>,
    loadingAudiences: !!audiencesRequestId,
  }),
);

export const selectChannels = createSelector(
  [selectCampaignManagement],
  ({ channels }) => channels as unknown as { id: number; name: string }[] | null,
);

export const selectVisibleLineItemChannels = createSelector(
  [selectVisibleCampaignManagementChannels],
  campaignManagementChannelsVisibleToWhitelabel =>
    LineItemChannels.filter(channel => campaignManagementChannelsVisibleToWhitelabel.some(vc => vc.name === channel)),
);
